import React from 'react';
import {interpolate, useCurrentFrame} from 'remotion';
import {ArrowDownLeft, ArrowUpRight, Play} from 'lucide-react';
import {brand, brandFont} from '../../brand';

const WORK = '#4CAF50';
const REST = '#D4A24A';

type Row =
  | {kind: 'simple'; label: 'WORK' | 'REST'; value: string}
  | {
      kind: 'repeat';
      repeat: string;
      total: string;
      children: {label: 'WORK' | 'REST'; value: string}[];
    };

const ROWS: Row[] = [
  {kind: 'simple', label: 'WORK', value: '02 : 00'},
  {kind: 'simple', label: 'WORK', value: '01 : 00'},
  {
    kind: 'repeat',
    repeat: '4×',
    total: '05:00',
    children: [
      {label: 'WORK', value: '00 : 45'},
      {label: 'REST', value: '00 : 15'},
    ],
  },
  {kind: 'simple', label: 'REST', value: '01 : 00'},
  {kind: 'simple', label: 'WORK', value: '00 : 30'},
];

const STAGGER_START = 6;
const SLOT_GAP = 10;
const SLOT_SPAN = 16;

export const Story3ManualBuilder: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: '8px 4px',
      }}
    >
      {ROWS.map((row, i) => {
        const start = STAGGER_START + i * SLOT_GAP;
        const t = interpolate(frame, [start, start + SLOT_SPAN], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = t > 0 ? 1 : 0;
        const y = (1 - t) * 24;
        const wrapStyle: React.CSSProperties = {
          opacity,
          transform: `translateY(${y}px)`,
        };
        return (
          <div key={i} style={wrapStyle}>
            {row.kind === 'simple' ? (
              <IntervalRow label={row.label} value={row.value} />
            ) : (
              <RepeatGroup row={row} />
            )}
          </div>
        );
      })}
      <div style={{height: 18}} />
      <StartButton />
    </div>
  );
};

const IntervalRow: React.FC<{label: 'WORK' | 'REST'; value: string}> = ({
  label,
  value,
}) => {
  const accent = label === 'WORK' ? WORK : REST;
  const ArrowIcon = label === 'WORK' ? ArrowUpRight : ArrowDownLeft;
  return (
    <div
      style={{
        height: 84,
        padding: '0 18px',
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(255,255,255,0.04)',
        borderRadius: 22,
        border: '1.5px solid rgba(255,255,255,0.06)',
        gap: 12,
      }}
    >
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 12,
          background: `${accent}2E`,
          border: `1.5px solid ${accent}8C`,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontFamily: brandFont,
          fontWeight: 800,
          fontSize: 18,
          color: accent,
          letterSpacing: 1,
        }}
      >
        <ArrowIcon size={20} color={accent} strokeWidth={2.6} />
        {label}
      </div>
      <div style={{flex: 1}} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          fontFamily: brandFont,
        }}
      >
        <StepKey symbol="−" />
        <div
          style={{
            fontSize: 36,
            fontWeight: 700,
            color: brand.textPrimary,
            letterSpacing: 0.5,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </div>
        <StepKey symbol="+" />
      </div>
    </div>
  );
};

const StepKey: React.FC<{symbol: string}> = ({symbol}) => (
  <div
    style={{
      width: 36,
      height: 36,
      borderRadius: 12,
      background: 'rgba(255,255,255,0.06)',
      border: '1.5px solid rgba(255,255,255,0.08)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontFamily: brandFont,
      fontSize: 22,
      color: 'rgba(255,255,255,0.7)',
      fontWeight: 700,
    }}
  >
    {symbol}
  </div>
);

const RepeatGroup: React.FC<{row: Extract<Row, {kind: 'repeat'}>}> = ({row}) => (
  <div
    style={{
      padding: 12,
      borderRadius: 26,
      background: 'rgba(255,255,255,0.025)',
      border: '1.5px solid rgba(139,92,246,0.28)',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}
  >
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 4px',
        fontFamily: brandFont,
      }}
    >
      <div
        style={{
          padding: '6px 14px',
          borderRadius: 999,
          background: brand.primary,
          color: brand.textPrimary,
          fontSize: 18,
          fontWeight: 800,
          letterSpacing: 0.5,
        }}
      >
        {row.repeat}
      </div>
      <div
        style={{
          fontSize: 18,
          color: 'rgba(255,255,255,0.5)',
          fontWeight: 600,
        }}
      >
        Total {row.total}
      </div>
    </div>
    {row.children.map((child, i) => (
      <IntervalRow key={i} label={child.label} value={child.value} />
    ))}
  </div>
);

const StartButton: React.FC = () => (
  <div
    style={{
      padding: '26px 32px',
      borderRadius: 32,
      background: brand.primary,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 14,
      fontFamily: brandFont,
      fontWeight: 700,
      fontSize: 28,
      color: brand.textPrimary,
      boxShadow: `0 16px 50px ${brand.primary}55`,
    }}
  >
    <Play size={26} color={brand.textPrimary} strokeWidth={2.6} fill={brand.textPrimary} />
    Start
  </div>
);
